'use client'

import { useEffect } from 'react'

import { activeDelta, HEARTBEAT_SECONDS, TICK_SECONDS, type ActivityTarget } from '@/lib/activity/rules'

const endpoint = `${process.env.NEXT_PUBLIC_BASE_PATH ?? ''}/activity-api`
const inputEvents = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'wheel', 'touchstart'] as const

// What the current page is about (attempt, course, live class), declared by <TrackActivity> in the page; the tracker
// of the layout sends it with each heartbeat. Switching target first sends the seconds counted for the previous one.
let currentTarget: ActivityTarget | null = null
let flushTo: ((target: ActivityTarget | null) => void) | null = null

function setActivityTarget(target: ActivityTarget | null) {
  if (JSON.stringify(target) === JSON.stringify(currentTarget)) return
  flushTo?.(currentTarget)
  currentTarget = target
}

/** Counts the time of this page for `target` (null: time on the site only) while mounted. */
export function TrackActivity({ target }: { target: ActivityTarget | null }) {
  const key = JSON.stringify(target)
  useEffect(() => {
    setActivityTarget(JSON.parse(key) as ActivityTarget | null)
    return () => setActivityTarget(null)
  }, [key])
  return null
}

/**
 * Active time of logged-in users (layout): counts seconds while the tab is visible and the user active (input in the
 * last 2 minutes), sends them every 30 s of such time and when the page is hidden or left. The server bounds
 * everything (lib/activity/rules.ts), so a lost or duplicated heartbeat only shifts a few seconds.
 */
export function ActivityTracker() {
  useEffect(() => {
    let pending = 0
    let lastTick = Date.now()
    let lastInput = Date.now()

    const send = (target: ActivityTarget | null, beacon = false) => {
      const seconds = Math.floor(pending)
      if (seconds < 1) return
      pending -= seconds
      const body = JSON.stringify({ seconds, target })
      if (beacon && navigator.sendBeacon?.(endpoint, new Blob([body], { type: 'text/plain' }))) return
      fetch(endpoint, { method: 'POST', body, keepalive: true, credentials: 'same-origin' }).catch(() => undefined)
    }
    // `visible` forced when the page is being hidden: the time since the last tick was spent on it.
    const tick = (visible = document.visibilityState === 'visible') => {
      const now = Date.now()
      pending += activeDelta({ visible, now, lastTick, lastInput })
      lastTick = now
    }
    const onInput = () => {
      lastInput = Date.now()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        tick(true)
        send(currentTarget, true)
      } else {
        lastTick = lastInput = Date.now() // coming back to the tab is activity; the hidden time is not counted
      }
    }
    const onPageHide = () => {
      tick()
      send(currentTarget, true)
    }
    flushTo = (target) => {
      tick()
      send(target)
    }
    const timer = window.setInterval(() => {
      tick()
      if (pending >= HEARTBEAT_SECONDS) send(currentTarget)
    }, TICK_SECONDS * 1000)
    inputEvents.forEach((name) => window.addEventListener(name, onInput, { passive: true }))
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)

    return () => {
      window.clearInterval(timer)
      inputEvents.forEach((name) => window.removeEventListener(name, onInput))
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
      flushTo = null
    }
  }, [])
  return null
}
